import { Box, Container, Typography } from "@mui/material";
import { FC } from "react";
import { Helmet } from 'react-helmet-async'
import LayoutPlain from "~/pages/authentication/LayoutPlain";
import { LegalTextContent } from "./CustomerAgreement";

const PrivacyNotice: FC = () => {
  return (
    <LayoutPlain withSiginIn={true}>
      <Helmet>
        <title>Customer Agreement | OnData</title>
      </Helmet>
      <Box
        sx={{
          bgcolor: 'background.paper',
          minHeight: '100%',
          pt: 6,
          pb: 4,
          mb: 4,
        }}
      >
        <Container maxWidth="md">
          <Typography
            color="textPrimary"
            align="center"
            variant="h2"
            sx={{
              mt: 6,
            }}
          >
            OnData Privacy Notice
          </Typography>
          <Typography
            component="p"
            align="center"
            color="textSecondary"
            variant="subtitle1"
          >
            (Last Updated: August 16, 2021)
          </Typography>



          <LegalTextContent>
            <Typography
              color="textPrimary"
              variant="body1"
            >
              This Privacy Notice describes how we collect and use your personal information in relation to OnData websites, applications, products, services, and events (“OnData Offerings”) that reference this Privacy Notice. This Privacy Notice does not apply to any contents that are processed, stored, or hosted by our customers using OnData Offerings in connection with an OnData account. This Privacy Notice also does not apply to any products, services, websites, or content that are offered by third parties or have their own privacy notice.
            </Typography>


            <Typography
              color="textPrimary"
              id="personal-information-we-collect"
              variant="h3"
            >
              Personal Information We Collect
            </Typography>
            <Typography
              color="textPrimary"
              variant="body1"
            >
              We collect your personal information when we provide OnData Offerings to you. Here are the types of information we gather:

            </Typography>

            <Typography
              color="textPrimary"
              component="span"
              variant="body1"
            >
              <ul>
                <li>
                  Information You Give Us: We collect any information you provide in relation to OnData Offerings. Examples of such information include, but not limited to, your name, address, phone number and other similar contact information.
                </li>
                <li>
                  Automatic Information: We automatically collect certain types of information when you interact with OnData Offerings. Examples of such information include, but not limited to, your network public IP address, browser type, version and cookies, time zone and language setting, and interaction data associated with OnData Offerings.
                </li>
                <li>
                  Information from Other Sources: We might collect information about you from other sources, including service providers, partners, and publicly available sources. Examples of such information include, but not limited to, marketing, sales generation, and recruitment information, search results and links, and credit history information.
                </li>
              </ul>
            </Typography>

            <Typography
              color="textPrimary"
              id="usage-of-personal-information"
              variant="h3"
            >
              Usage of Personal Information
            </Typography>
            <Typography
              color="textPrimary"
              variant="body1"
            >
              We use your personal information to operate, provide, and improve OnData Offerings. Our purposes for using personal information include:

            </Typography>
            <Typography
              color="textPrimary"
              component="span"
              variant="body1"
            >
              <ul>
                <li>
                  Provide OnData Offerings: We use your personal information to provide and deliver OnData Offerings and process transactions related to OnData Offerings, including registrations, subscriptions, purchases, and payments.
                </li>
                <li>
                  Measure, Support, and Improve OnData Offerings: We use your personal information to measure use of, analyze performance of, fix errors in, provide support for, and improve OnData Offerings.
                </li>
                <li>
                  Personalization of OnData Offerings: We use your personal information to personalize your experience with OnData Offerings.
                </li>
                <li>
                  Communicate with You: We use your personal information to communicate with you in relation to OnData Offerings via different channels (e.g., by phone, email, chat) and to respond to your requests.
                </li>
                <li>
                  Fraud and Abuse Prevention and Credit Risks: We use your personal information to prevent and detect fraud and abuse. We may also use scoring methods to assess and manage credit risks.
                </li>
                <li>
                  Your Consent: We may also ask for your consent to use your personal information for a specific purpose that we communicate to you.
                </li>
              </ul>
            </Typography>


            <Typography
              color="textPrimary"
              id="sharing-of-personal-information"
              variant="h3"
            >
              Sharing of Personal Information
            </Typography>
            <Typography
              color="textPrimary"
              variant="body1"
            >
              Information about our customers is an important part of our business and we are not in the business of selling our customers’ personal information to others. We may share personal information only as described below.

            </Typography>
            <Typography
              color="textPrimary"
              component="span"
              variant="body1"
            >
              <ul>
                <li>
                  Third-Party Service Providers: We employ other companies and individuals to perform functions on our behalf. Examples of sharing include, but not limited to: authenticating logins, processing payments, assessing credit and compliance risks, analyzing data, providing marketing and sales assistance, conducting customer relationship management, and providing training. These third-party service providers have access to personal information to perform their functions but may not use it for other purposes.
                </li>
                <li>
                  Protection of OnData and Others: We release account and other personal information when we believe release is appropriate to comply with the law, enforce or apply our terms and other agreements, or protect the rights, property, or security of OnData, our customers, or others. This includes exchanging information with other companies and organizations for fraud prevention and detection and credit risk reduction.
                </li>
              </ul>
            </Typography>

            <Typography
              color="textPrimary"
              id="security-of-personal-information"
              variant="h3"
            >
              Security of Personal Information
            </Typography>
            <Typography
              color="textPrimary"
              variant="body1"
            >
              At OnData, security is our highest priority. We design our systems with your security and privacy in mind.

            </Typography>
            <Typography
              color="textPrimary"
              component="span"
              variant="body1"
            >
              <ul>
                <li>
                  We protect the security of your information during transmission to or from OnData websites, applications, products, or services by using encryption protocols and software.
                </li>
                <li>
                  We maintain physical, electronic, and procedural safeguards in connection with the collection, storage, and disclosure of personal information. Our security procedures mean that we may request proof of identity before we disclose personal information to you.
                </li>
              </ul>
            </Typography>

            <Typography
              color="textPrimary"
              id="access-to-personal-information"
              variant="h3"
            >
              Access to Personal Information
            </Typography>
            <Typography
              color="textPrimary"
              variant="body1"
            >
              You can view, update, and delete certain information about your account and your interactions with OnData Offerings. If you cannot access or update your information yourself, you can always contact us for assistance.
            </Typography>


            <Typography
              color="textPrimary"
              id="retention-of-personal-information"
              variant="h3"
            >
              Retention of Personal Information
            </Typography>
            <Typography
              color="textPrimary"
              variant="body1"
            >
              We keep your personal information to enable your continued use of OnData Offerings, for as long as it is required to fulfill the relevant purposes described in this Privacy Notice, as may be required by law (including for tax and accounting purposes), or as otherwise communicated to you. How long we retain specific personal information varies depending on the purpose for its use, and we will delete your personal information in accordance with applicable law.
            </Typography>


            <Typography
              color="textPrimary"
              id="contacts-and-revisions"
              variant="h3"
            >
              Contacts and Revisions
            </Typography>
            <Typography
              color="textPrimary"
              variant="body1"
            >
              If you have any concern about privacy at OnData, please contact us with a thorough description, and we will try to resolve the issue for you. Our mailing address is 9600 Great Hills Trail, Suite 150W, Austin, TX 78759. Our business changes constantly, and our Privacy Notice may also change. You should check our website frequently to see recent changes. You can see the date on which the latest version of this Privacy Notice was posted. Unless stated otherwise, our current Privacy Notice applies to all personal information we have about you and your account.
            </Typography>

          </LegalTextContent>

        </Container>
      </Box>
    </LayoutPlain>
  )
}

export default PrivacyNotice;
